import { useState } from "react";
import { TextField, MenuItem } from "@mui/material";
import { useSnackbar } from "notistack";
import QuickCreateModal from "../common/QuickCreateModal";
import { useCreateClientMutation } from "../../features/masterdata/masterdataApi";
import type { Client, ClientCreate, ClientType } from "../../features/masterdata/types";

const CLIENT_TYPES: { value: ClientType; label: string }[] = [
  { value: "individual", label: "Individual" },
  { value: "hotel", label: "Hotel" },
  { value: "hotel_group", label: "Hotel group" },
  { value: "company", label: "Company" },
];

export interface QuickCreateClientProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (client: Client) => void;
}

export default function QuickCreateClient({ open, onClose, onSuccess }: QuickCreateClientProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [createClient, { isLoading }] = useCreateClientMutation();

  const [name, setName] = useState("");
  const [type, setType] = useState<ClientType>("hotel");

  const handleSubmit = async (): Promise<Client> => {
    const payload: ClientCreate = { name: name.trim(), type };
    const created = await createClient(payload).unwrap();
    enqueueSnackbar("Client created", { variant: "success" });
    return created;
  };

  return (
    <QuickCreateModal<Client>
      open={open}
      onClose={onClose}
      title="New client"
      onSubmit={handleSubmit}
      onSuccess={(c) => {
        setName("");
        setType("hotel");
        onSuccess(c);
      }}
      submitDisabled={!name.trim()}
      loading={isLoading}
      submitLabel="Save & select"
    >
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        fullWidth
        autoFocus
        placeholder="e.g. Acme Hotel"
      />
      <TextField
        label="Type"
        select
        value={type}
        onChange={(e) => setType(e.target.value as ClientType)}
        fullWidth
      >
        {CLIENT_TYPES.map((t) => (
          <MenuItem key={t.value} value={t.value}>
            {t.label}
          </MenuItem>
        ))}
      </TextField>
    </QuickCreateModal>
  );
}
